import { useMemo } from 'react';
import { useAngularContext } from 'AngularContext';
import { type StreamIguanaClass, type StreamSnakeCasedAttrs, type StreamIguanaObject } from 'Lessons';
import { type CurrentUser } from 'Users';
import { type BrowseStream, type StreamAccess } from '../BrowseCourses.types';

type AccessHelperClass = { new: (stream: StreamIguanaObject) => StreamAccess };

/*
    ContentAccessHelper needs Iguana streams. Clone each record, because RTK Query freezes its state and
    Iguana mutates attributes. Keep the default Iguana caching: ContentAccessHelper's prerequisite checks
    read the Stream caches. `currentUser` is only a memo dependency, because ContentAccessHelper reads the
    Iguana user from $rootScope.
*/
export function useStreamAccess(streams: BrowseStream[], currentUser: CurrentUser | null) {
    const $injector = useAngularContext();

    return useMemo(() => {
        const accessByStreamId = new Map<string, StreamAccess>();
        const StreamClass = $injector.get<StreamIguanaClass>('Lesson.Stream');
        const ContentAccessHelper = $injector.get<AccessHelperClass>('ContentAccessHelper');

        // Cache every stream before any access check. An exam's prerequisite check reads the other streams
        // from the cache, and a prerequisite can come after the exam in the response.
        const iguanaStreams = streams.map(stream =>
            StreamClass.new(structuredClone(stream) as unknown as StreamSnakeCasedAttrs<false>),
        );
        iguanaStreams.forEach(iguanaStream => {
            const helper = ContentAccessHelper.new(iguanaStream);
            accessByStreamId.set(iguanaStream.id, {
                canLaunch: helper.canLaunch,
                reason: helper.reason,
                reasonTitle: helper.reasonTitle,
                reasonMessage: helper.reasonMessage,
            });
        });

        return accessByStreamId;
    }, [streams, currentUser, $injector]);
}
